import { useState } from 'react';

type GenericArgumentType<A> = A extends any[] ? A : [A];

/**
 * @param fn your async function that you want loading/error states for.
 * @param onError optional callback function if you want side effects on errors.
 *
 * Helper for getting loading and error states of async functions that's useful
 * for views that don't change much based on loading/error states.
 * For views that change a lot based on initial/loading/error/succes states, check out RemoteData in aatos-ui
 * return properties:
 * - asyncFn: use this to call your async operation
 * - data: result of the asyncFn return
 * - loading: boolean, if request is loading
 * - error: boolean, if request failed (it's reset on retry)
 * - resetState: resets the state to initial state
 * @example
 * // with renaming asyncFn
 * const { asyncFn: handleButtonClick, data, loading, error } = useAsyncFunction(async() => {...})
 */
export const useAsyncFunction = <T, Args = []>(
  fn: (...args: GenericArgumentType<Args>) => Promise<T>,
  onError?: (e?: unknown) => void,
) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState<T | null>(null);

  const asyncFn = async (...args: GenericArgumentType<Args>): Promise<T> => {
    setLoading(true);
    setError(false);
    try {
      const response = await fn(...args);
      setData(response);
      setLoading(false);
      setError(false);
      return response;
    } catch (e) {
      console.error(e);
      setLoading(false);
      setError(true);
      if (onError) onError(e); // have this as last if we want to also throw error
      throw e;
    }
  };

  return {
    asyncFn,
    data,
    loading,
    error,
    setData,
    resetState: () => {
      setData(null);
      setLoading(false);
      setError(false);
    },
  };
};
